/**
 * CRUD for reusable import mapping presets (import_mappings table). Presets are
 * private to their author unless published project-wide (admin only — enforced
 * by the DB trigger).
 */
import { supabase } from '@/lib/supabase/client'
import type { MappingConfig } from '../lib/mapping-types'

export interface MappingPreset {
  id: string
  name: string
  description: string | null
  visibility: 'private' | 'project'
  config: MappingConfig
  version: number
  ownerId: string
  updatedAt: string
}

export async function listPresets(formId: string): Promise<MappingPreset[]> {
  const { data, error } = await supabase
    .from('import_mappings')
    .select('id, name, description, visibility, config, version, owner_id, updated_at')
    .eq('form_id', formId)
    .eq('is_deleted', false)
    .order('updated_at', { ascending: false })
  if (error) throw error
  return (data ?? []).map((r) => ({
    id: r.id,
    name: r.name,
    description: r.description,
    visibility: r.visibility as 'private' | 'project',
    config: r.config as unknown as MappingConfig,
    version: r.version,
    ownerId: r.owner_id,
    updatedAt: r.updated_at,
  }))
}

export async function createPreset(args: {
  projectId: string
  formId: string
  userId: string
  name: string
  description?: string
  visibility: 'private' | 'project'
  config: MappingConfig
}): Promise<{ id: string }> {
  const { data, error } = await supabase
    .from('import_mappings')
    .insert({
      project_id: args.projectId,
      form_id: args.formId,
      owner_id: args.userId,
      created_by: args.userId,
      name: args.name,
      description: args.description ?? null,
      visibility: args.visibility,
      config: args.config,
    })
    .select('id')
    .single()
  if (error) throw error
  return { id: data.id }
}

export async function updatePreset(args: {
  id: string
  userId: string
  expectedVersion: number
  name?: string
  description?: string
  visibility?: 'private' | 'project'
  config?: MappingConfig
}): Promise<{ version: number }> {
  const patch: Record<string, unknown> = { updated_by: args.userId }
  if (args.name !== undefined) patch.name = args.name
  if (args.description !== undefined) patch.description = args.description
  if (args.visibility !== undefined) patch.visibility = args.visibility
  if (args.config !== undefined) patch.config = args.config

  const { data, error } = await supabase
    .from('import_mappings')
    .update(patch)
    .eq('id', args.id)
    .eq('version', args.expectedVersion)
    .select('version')
    .maybeSingle()
  if (error) throw error
  if (!data) throw new Error('تعارض نسخه: نگاشت توسط کاربر دیگری تغییر کرده است.')
  return { version: data.version }
}

export async function deletePreset(id: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('import_mappings')
    .update({ is_deleted: true, deleted_at: new Date().toISOString(), deleted_by: userId })
    .eq('id', id)
  if (error) throw error
}
